import type {
  AdminSponsorshipApplication,
  AdminSponsorshipApplicationPage,
  AdminSponsorshipResponse,
} from '@inflip/contracts/admin';
import { SponsorshipApplicationStatus } from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { qualifiedChannelLabel } from './sponsorship-application-cells';
import { Button, ConfirmDialog, Dialog, ErrorBanner, LoadingRow, PromptDialog } from './ui';
import { api } from '../lib/api-client';
import { SPONSORSHIP_APPLICATION_STATUS_META, SPONSORSHIP_STATUS_META } from '../lib/status-ui';
import { formatDate } from '../lib/time-zone';

/** 한 협찬의 지원 — 이 정도면 한 번에 다 본다 */
const APPLICATION_LIMIT = 100;

type Decision = { application: AdminSponsorshipApplication; approve: boolean };

/**
 * 회원 상세의 "등록한 협찬" — 그 회원이 올린 협찬마다 지원 현황을 펼쳐 보고,
 * 등록자 대신 어드민이 지원을 승인·거절할 수 있다 (등록자와 같은 규칙, 지원자에게 같은 알림)
 */
export function OwnedSponsorships({
  token,
  sponsorships,
}: {
  token: string;
  sponsorships: AdminSponsorshipResponse[];
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <ul className="divide-y divide-border/40">
      {sponsorships.map((s) => {
        const status = SPONSORSHIP_STATUS_META[s.status];
        const open = openId === s.id;
        return (
          <li key={s.id}>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpenId(open ? null : s.id)}
              className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-md px-2 py-3 text-left transition hover:bg-muted"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold">{s.title}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {s.businessName} · {formatDate(s.createdAt)} 등록
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className={`text-xs font-bold ${status.cls}`}>{status.label}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  지원 {s.applicationCount.toLocaleString('ko-KR')}명
                </p>
              </div>
              <svg
                aria-hidden
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={`shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            {open && <SponsorshipApplications token={token} sponsorship={s} />}
          </li>
        );
      })}
    </ul>
  );
}

/** 협찬 하나에 들어온 지원 — 상태별 개수, 지원자 목록, 승인 대기 건의 대리 승인·거절 */
function SponsorshipApplications({
  token,
  sponsorship,
}: {
  token: string;
  sponsorship: AdminSponsorshipResponse;
}) {
  const [result, setResult] = useState<AdminSponsorshipApplicationPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [messageOf, setMessageOf] = useState<AdminSponsorshipApplication | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    return api
      .listSponsorshipApplications(token, {
        sponsorshipId: sponsorship.id,
        pageSize: APPLICATION_LIMIT,
      })
      .then(setResult)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [token, sponsorship.id]);
  useEffect(() => {
    void load();
  }, [load]);

  /** 거절은 사유(reason)를 함께 — 지원자에게 보인다 */
  const decide = async ({ application, approve }: Decision, reason?: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await (approve
        ? api.approveSponsorshipApplication(token, application.id)
        : api.rejectSponsorshipApplication(token, application.id, reason ?? ''));
      setDecision(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const items = result?.items ?? [];
  const counts = Object.values(SponsorshipApplicationStatus).map((status) => ({
    status,
    count: items.filter((a) => a.status === status).length,
  }));

  return (
    <div className="mb-3 rounded-lg bg-muted/50 px-4 py-3">
      {error && <ErrorBanner message={error} />}
      {!result && !error && <LoadingRow />}
      {result && items.length === 0 && (
        <p className="py-2 text-xs text-muted-foreground">아직 지원이 없어요.</p>
      )}
      {items.length > 0 && (
        <>
          <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
            {counts.map(({ status, count }) => (
              <span key={status} className={SPONSORSHIP_APPLICATION_STATUS_META[status].cls}>
                {SPONSORSHIP_APPLICATION_STATUS_META[status].label}{' '}
                <span className="font-bold tabular-nums">{count}</span>
              </span>
            ))}
          </p>
          <ul className="mt-2 divide-y divide-border/40">
            {items.map((a) => {
              const meta = SPONSORSHIP_APPLICATION_STATUS_META[a.status];
              return (
                <li key={a.id} className="flex items-center gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/users/$userId"
                      params={{ userId: a.applicantId }}
                      search={{ tab: 'sponsorships' }}
                      className="text-sm font-semibold hover:text-brand hover:underline"
                    >
                      {a.applicantName}
                    </Link>
                    <span className="ml-1.5 text-xs text-muted-foreground">{a.applicantEmail}</span>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {formatDate(a.createdAt)} 지원
                      {a.qualifiedPlatform && ` · ${qualifiedChannelLabel(a)}`}
                      {a.visitDate && ` · 방문 ${a.visitDate}`}
                    </p>
                    {a.rejectReason && (
                      <p className="mt-0.5 text-xs text-rose-600">거절 사유 · {a.rejectReason}</p>
                    )}
                  </div>
                  {a.message && (
                    <Button variant="ghost" size="sm" onClick={() => setMessageOf(a)}>
                      메시지
                    </Button>
                  )}
                  {a.status === SponsorshipApplicationStatus.PENDING ? (
                    <div className="flex shrink-0 gap-1.5">
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => setDecision({ application: a, approve: true })}
                      >
                        승인
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={busy}
                        onClick={() => setDecision({ application: a, approve: false })}
                      >
                        거절
                      </Button>
                    </div>
                  ) : (
                    <span className={`shrink-0 text-xs font-bold ${meta.cls}`}>{meta.label}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {decision?.approve && (
        <ConfirmDialog
          title="지원을 승인할까요?"
          description={`${decision.application.applicantName}님의 "${sponsorship.title}" 지원을 등록자 대신 승인해요. 지원자에게 알림이 가요.`}
          confirmLabel="승인"
          busy={busy}
          onConfirm={() => void decide(decision)}
          onClose={() => setDecision(null)}
        />
      )}
      {decision && !decision.approve && (
        <PromptDialog
          title="지원을 거절할까요?"
          description={`${decision.application.applicantName}님의 "${sponsorship.title}" 지원을 등록자 대신 거절해요. 사유는 지원자에게 알림과 지원 상세로 그대로 보여요.`}
          placeholder="거절 사유 (2~500자)"
          submitLabel="거절"
          danger
          busy={busy}
          minLength={2}
          onSubmit={(reason) => void decide(decision, reason)}
          onClose={() => setDecision(null)}
        />
      )}
      {messageOf && (
        <Dialog label="지원 메시지" onClose={() => setMessageOf(null)}>
          <p className="text-lg font-bold tracking-tight">지원 메시지</p>
          <p className="mt-1 text-xs text-muted-foreground">{messageOf.applicantName}</p>
          <p className="mt-4 max-h-[50vh] overflow-y-auto whitespace-pre-wrap rounded-md bg-muted px-3.5 py-3 text-sm leading-relaxed">
            {messageOf.message}
          </p>
          <Button variant="secondary" className="mt-5 w-full" onClick={() => setMessageOf(null)}>
            닫기
          </Button>
        </Dialog>
      )}
    </div>
  );
}
